import { tenantSlugFromHostname } from "@/lib/tenant";
import type { TenantSlug } from "@/lib/theme-presets";

export interface GoogleDrivePickerConfig {
  enabled: boolean;
  oauthClientId: string;
  apiKey: string;
  appId: string;
  /**
   * ADR 0025. The Drive folder the chooser opens into, when the course has a
   * `ready` institutional container. Unlike every field above it this one is
   * per-course rather than per-host, so nothing in this module ever sets it:
   * the caller that knows the course spreads it onto the resolved config. An
   * absent or unusable value leaves the chooser exactly where ADR 0009 put it,
   * on the person's own Drive.
   */
  parentFolderId?: string | null;
  /**
   * Exact Google OIDC subject already verified for this IMAM connection.
   * Google may use it to skip account selection. Email is never accepted as
   * an identity hint or binding.
   */
  providerSubjectHint?: string | null;
}

type PickerEnvironmentName =
  | "VITE_GOOGLE_DRIVE_MURTADA_PICKER_ENABLED"
  | "VITE_GOOGLE_DRIVE_MURTADA_OAUTH_CLIENT_ID"
  | "VITE_GOOGLE_DRIVE_MURTADA_PICKER_API_KEY"
  | "VITE_GOOGLE_DRIVE_MURTADA_PICKER_APP_ID"
  | "VITE_GOOGLE_DRIVE_MUFID_PICKER_ENABLED"
  | "VITE_GOOGLE_DRIVE_MUFID_OAUTH_CLIENT_ID"
  | "VITE_GOOGLE_DRIVE_MUFID_PICKER_API_KEY"
  | "VITE_GOOGLE_DRIVE_MUFID_PICKER_APP_ID";

type PickerEnvironment = Partial<Record<PickerEnvironmentName, string>>;

type PickerEnvironmentFields = Readonly<{
  enabled: PickerEnvironmentName;
  oauthClientId: PickerEnvironmentName;
  apiKey: PickerEnvironmentName;
  appId: PickerEnvironmentName;
}>;

const PICKER_ENVIRONMENT_FIELDS: Readonly<Record<TenantSlug, PickerEnvironmentFields>> = {
  murtada: {
    enabled: "VITE_GOOGLE_DRIVE_MURTADA_PICKER_ENABLED",
    oauthClientId: "VITE_GOOGLE_DRIVE_MURTADA_OAUTH_CLIENT_ID",
    apiKey: "VITE_GOOGLE_DRIVE_MURTADA_PICKER_API_KEY",
    appId: "VITE_GOOGLE_DRIVE_MURTADA_PICKER_APP_ID",
  },
  mufid: {
    enabled: "VITE_GOOGLE_DRIVE_MUFID_PICKER_ENABLED",
    oauthClientId: "VITE_GOOGLE_DRIVE_MUFID_OAUTH_CLIENT_ID",
    apiKey: "VITE_GOOGLE_DRIVE_MUFID_PICKER_API_KEY",
    appId: "VITE_GOOGLE_DRIVE_MUFID_PICKER_APP_ID",
  },
};

const DISABLED: GoogleDrivePickerConfig = Object.freeze({
  enabled: false,
  oauthClientId: "",
  apiKey: "",
  appId: "",
});

function readTenantGoogleDrivePickerConfig(
  env: PickerEnvironment,
  fields: PickerEnvironmentFields,
): GoogleDrivePickerConfig {
  const enabledValue = env[fields.enabled]?.trim().toLowerCase();
  if (enabledValue !== "true") return DISABLED;

  const oauthClientId = env[fields.oauthClientId]?.trim() ?? "";
  const apiKey = env[fields.apiKey]?.trim() ?? "";
  const appId = env[fields.appId]?.trim() ?? "";
  if (
    !/^\d+-[a-z0-9-]+\.apps\.googleusercontent\.com$/.test(oauthClientId)
    || !apiKey
    || apiKey.length > 256
    || !/^\d{6,20}$/.test(appId)
    || !oauthClientId.startsWith(`${appId}-`)
  ) {
    return DISABLED;
  }
  return { enabled: true, oauthClientId, apiKey, appId };
}

export function readGoogleDrivePickerConfigs(
  env: PickerEnvironment,
): Readonly<Record<TenantSlug, GoogleDrivePickerConfig>> {
  return {
    murtada: readTenantGoogleDrivePickerConfig(env, PICKER_ENVIRONMENT_FIELDS.murtada),
    mufid: readTenantGoogleDrivePickerConfig(env, PICKER_ENVIRONMENT_FIELDS.mufid),
  };
}

/**
 * Resolve browser-visible credentials only after the trusted exact-host tenant
 * boundary has resolved. Unknown hosts and malformed tenant entries fail closed
 * without falling back to the other Workspace.
 */
export function googleDrivePickerConfigForHostname(
  hostname: string = window.location.hostname,
  env: PickerEnvironment = import.meta.env,
): GoogleDrivePickerConfig {
  const tenant = tenantSlugFromHostname(hostname);
  if (tenant === null) return DISABLED;
  return readGoogleDrivePickerConfigs(env)[tenant];
}
